/**
 * Boot: selectBridge → bridge.ready → identity → store.session, then the TableDriver
 * (`?driver=script&script=<url>` → ScriptedDriver, else ColyseusDriver, not yet connected: the
 * Lobby calls `connect`). `?e2e=1` exposes `window.__spades = { store, cueQueue, bridge, driver,
 * script }` for Playwright; the scene extends the same object.
 */
import { ColyseusDriver } from '../net/ColyseusDriver';
import { cueQueue } from '../net/cues';
import { ScriptedDriver, type TableScript } from '../net/ScriptedDriver';
import type { TableDriver } from '../net/TableDriver';
import { wireDriver } from '../net/wire';
import { store } from '../store/store';
import type { ActivityBridge } from './ActivityBridge';
import { type Identity, establishIdentity } from './identity';
import { selectBridge } from './selectBridge';

export interface AppRuntime {
  bridge: ActivityBridge;
  identity: Identity | null;
  driver: TableDriver;
  script: ScriptedDriver | null;
  unwire: () => void;
  dispose: () => void;
}

export interface SpadesTestApi {
  store: typeof store;
  cueQueue: typeof cueQueue;
  bridge?: ActivityBridge;
  driver?: TableDriver;
  script?: ScriptedDriver | null;
  [extra: string]: unknown;
}

declare global {
  interface Window {
    __spades?: SpadesTestApi;
  }
}

let runtime: AppRuntime | null = null;
let booting: Promise<AppRuntime> | null = null;

export function getRuntime(): AppRuntime | null {
  return runtime;
}

export function isE2E(search: string = globalThis.location?.search ?? ''): boolean {
  return new URLSearchParams(search).get('e2e') === '1';
}

/** Idempotent (StrictMode / HMR safe). */
export function boot(): Promise<AppRuntime> {
  booting ??= doBoot();
  return booting;
}

async function doBoot(): Promise<AppRuntime> {
  const s = store.getState();
  const bridge = await selectBridge();
  await bridge.ready();
  s.setSession({ platform: bridge.kind === 'web' ? 'web' : 'discord' });

  const offs: Array<() => void> = [];

  let identity: Identity | null = null;
  try {
    identity = await establishIdentity(bridge);
    s.setSession({ me: identity.user, token: identity.token, error: null, authNotice: identity.notice ?? null });
  } catch (err) {
    s.setSession({ error: err instanceof Error ? err.message : String(err) });
  }

  // AFTER `establishIdentity`, which is what authenticates the Discord frame: the SDK gates
  // SUBSCRIBE behind an authenticated frame, so subscribing earlier rejected silently and the
  // layout-mode and participant events never arrived. The visible symptom was a full HUD rendered
  // into a 320x180 PIP thumbnail, and an empty lobby roster, for the whole session.
  offs.push(
    bridge.subscribeParticipants((participants) => store.getState().setSession({ participants })),
    bridge.subscribeLayoutMode((layoutMode) => store.getState().setPresentation({ layoutMode })),
    bridge.subscribeOrientation((orientation) => store.getState().setPresentation({ orientation })),
  );

  if (bridge.voiceAvailable()) {
    offs.push(
      bridge.subscribeVoice((u) => {
        const st = store.getState();
        if (u.speaking !== undefined) st.setSpeaking(u.userId, u.speaking);
        if (u.muted !== undefined) st.setMuted(u.userId, u.muted);
      }),
    );
  }

  const { driver, script } = await createDriver(bridge, identity);
  const unwire = wireDriver(driver, store, cueQueue);
  if (script) void script.connect();

  runtime = {
    bridge,
    identity,
    driver,
    script,
    unwire,
    dispose: () => {
      unwire();
      offs.forEach((off) => off());
      driver.leave();
    },
  };

  if (isE2E()) {
    window.__spades = Object.assign(window.__spades ?? { store, cueQueue }, { store, cueQueue, bridge, driver, script });
  }
  return runtime;
}

async function createDriver(
  bridge: ActivityBridge,
  identity: Identity | null,
): Promise<{ driver: TableDriver; script: ScriptedDriver | null }> {
  const params = new URLSearchParams(globalThis.location?.search ?? '');
  if (params.get('driver') === 'script') {
    const url = params.get('script');
    let tableScript: TableScript = { steps: [] };
    if (url) {
      try {
        tableScript = await ScriptedDriver.load(url);
      } catch (err) {
        console.warn('[boot] script load failed, using empty script', err);
      }
    }
    const script = new ScriptedDriver(tableScript, { autoplay: params.get('autoplay') !== '0' });
    return { driver: script, script };
  }
  const driver = new ColyseusDriver({
    surface: bridge.kind === 'web' ? 'web' : 'discord',
    token: identity?.token ?? null,
    instanceId: bridge.instanceId(),
    guildId: bridge.guildId(),
    channelId: bridge.channelId(),
    // Resync from what the scene has actually shown, not from what arrived (see CueQueue).
    appliedSeq: () => cueQueue.lastAppliedSeq,
  });
  return { driver, script: null };
}
